/* The dashboard: what the person signed in should be looking at.
 *
 * That is a different question depending on who they are, so the screen is two
 * screens stacked.
 *
 *  - **Everybody** gets the shop's notice board and their own bench: what the
 *    office has put up, and the jobs they are on. For a sprayer that is the
 *    whole of it, and it is the honest whole — somebody opening the app in the
 *    booth wants their work, not the shop's numbers.
 *  - **The office** gets the shop at a glance underneath: five figures and four
 *    charts over every job in the shop. The server is what decides they get it
 *    (`dash.shop` is null for anyone else), and `isOffice` here is the
 *    presentation half — stricter than the server, never looser, the way
 *    `canEdit` is.
 *
 * The charts are drawn from the design tokens and from the shop's own colours,
 * and never from a palette invented here. That is not a style preference: the
 * interface is achromatic because the thing being made *is* a colour
 * (IDEA.md §10), so a stage bar is the tone the admin gave that stage and a
 * colour bar is the paint. Colour follows the thing it names, so filtering or
 * reordering can never repaint it into somebody else's — and every mark carries
 * its name and its number as text beside it, because status is never colour
 * alone and a chart you cannot read the values off is a chart the shop stops
 * trusting.
 *
 * No charting library. Every mark here is a div with a width or a height, which
 * is all a bar is; the app ships React, ReactDOM and Zustand and this is not
 * reason enough to add a fourth. */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { DASHBOARD_WEEKS, NOTICE_LEVEL_LABEL } from '@oneshot2pac/shared';
import { overdueDay } from '@/domain/schedule';
import { formatDay, formatDayShort, formatInstant, isWeekend, weekdayOf } from '@/domain/time';
import { isOffice, seesCustomers, stageName, toneOf, useApp } from '@/store/useApp';
import { Empty, PageHead, Panel, PanelHead } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import { useNarrowerThan, usePageGutter, withGutter } from '@/ui/viewport';
import type {
  ColourLoadDto,
  CustomerLoadDto,
  DayLoadDto,
  IsoDay,
  IsoInstant,
  NoticeDto,
  ShopOverviewDto,
  StageLoadDto,
  WeekLoadDto,
} from '@/types';

/* Below this the two-up panels — the colours on the racks and who the shop is
 * working for — fold to one column. It is what those two panels need to keep
 * their label, their bar and their two numbers on one line, twice over, plus
 * the page's gutter and the gap between them. Not a device. */
const TWO_COL = 900;

/** How tall a column chart's plot is. The axis labels sit under it and are not
 *  part of it — a container sized to the plot alone crops its own axis and
 *  grows a scrollbar nobody asked for. */
const PLOT_HEIGHT = 96;

/** The most a bar chart's name column is worth. Past this the labels take the
 *  panel and the bars become a margin — and the bar is the thing being read. */
const BAR_LABEL = 210;

/** The widest a column is drawn, however much room the panel has. A fortnight
 *  across a desktop panel is otherwise fourteen boxes 90px wide, which reads as
 *  a table of empty cells rather than as a chart of a quiet fortnight. */
const COLUMN_WIDTH = 46;

/** How the shop says the days of the week, for an axis that has room for three
 *  letters and nothing else. */
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function DashboardScreen() {
  const st = useApp();
  const office = isOffice(st);
  const loadDashboard = st.loadDashboard;
  const markNoticesRead = st.markNoticesRead;

  // The screen is a request, so opening it makes one. It is not loaded on the
  // way in the way the notice board is: the count on the tab needs the board
  // from every screen, but nothing outside this one reads a chart.
  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  /* What was new when this screen opened, held for as long as it is open.
     Marking the notices read is the point of arriving here, but doing it and
     then drawing from the answer would take the "New" marks off the page while
     somebody was still reading them — the badge would clear, which is right,
     and the reason it cleared would vanish, which is not. So the ids are kept
     as they were found and the server is told separately. */
  const wasNew = useRef<Set<string> | null>(null);
  if (wasNew.current === null && st.noticesStatus === 'ready') {
    wasNew.current = new Set(st.notices.filter((n) => !n.read).map((n) => n.id));
  }

  useEffect(() => {
    void markNoticesRead();
  }, [markNoticesRead, st.noticesStatus]);

  const dash = st.dash;
  const shop = office ? (dash?.shop ?? null) : null;
  const today = dash?.today ?? null;
  // The chrome's own gutter, so the title lines up with the brand above it and
  // comes in with the header on a tablet.
  const gutter = usePageGutter();

  return (
    <section style={{ flex: 1, padding: withGutter(gutter, 0, 40) }}>
      <PageHead
        title="Dashboard"
        caption={st.dashError || caption(st.session?.user.name ?? '', today)}
        urgent={Boolean(st.dashError)}
      >
        {office ? (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginLeft: 'auto', height: 48 }}
            onClick={st.openNewNotice}
          >
            Put a notice up
          </button>
        ) : null}
      </PageHead>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <NoticeBoard wasNew={wasNew.current} />

        {shop ? <Figures shop={shop} /> : null}

        <MyBench />

        {shop ? (
          <>
            <StageLoad rows={shop.stages} />
            <PromisedAhead rows={shop.promised} today={dash?.today ?? ''} />
            <TwoUp>
              <ColourLoad rows={shop.colours} />
              <CustomerLoad rows={shop.customers} />
            </TwoUp>
            <TakenIn rows={shop.takenIn} />
          </>
        ) : null}
      </div>
    </section>
  );
}

/** The one line under the title: who is looking, and what day the shop is on.
 *
 *  The day comes off the answer rather than off the iPad's clock — the shop's
 *  timezone is a column on the organisation precisely so that a tablet somebody
 *  brought back from Perth does not put the board on yesterday. */
function caption(name: string, today: IsoDay | null): string {
  const first = name.trim().split(/\s+/)[0];
  const day = today === null ? '' : formatDay(today);
  if (!first) return day;
  return day ? `${first} — ${day}` : first;
}

/* — the notice board —
 *
 * What the corner of the whiteboard used to say. The office writes it and the
 * whole shop reads it, subcontractors included: a notice about the roller door
 * is for whoever walks through it. */

function NoticeBoard({ wasNew }: { wasNew: Set<string> | null }) {
  const st = useApp();
  const office = isOffice(st);
  const notices = st.notices;

  return (
    <Panel>
      <PanelHead
        title="Notice board"
        meta={notices.length === 0 ? undefined : `${notices.length} up`}
      />
      {st.noticesError ? (
        <Empty>{st.noticesError}</Empty>
      ) : notices.length === 0 ? (
        <Empty>
          {office
            ? 'Nothing on the board. Put a notice up when the shop needs to know something — the run is off Friday, the new topcoat is in.'
            : 'Nothing on the board. The office puts a notice up here when the shop needs to know something.'}
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notices.map((n) => (
            <NoticeRow key={n.id} notice={n} isNew={wasNew?.has(n.id) ?? false} office={office} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function NoticeRow({
  notice,
  isNew,
  office,
}: {
  notice: NoticeDto;
  isNew: boolean;
  office: boolean;
}) {
  const openEditNotice = useApp((s) => s.openEditNotice);
  const takeNoticeDown = useApp((s) => s.takeNoticeDown);
  const urgent = notice.level === 'urgent';

  return (
    <div
      className="blueprint"
      style={{
        padding: '12px 14px',
        // The urgent tint is the app's own "look at this", and the word Urgent
        // is drawn beside it — colour never carries a status on its own.
        background: urgent ? 'var(--urgent-tint)' : 'var(--surface-2)',
        borderColor: urgent ? 'var(--urgent)' : 'var(--line)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
        <Tag tone={urgent ? 'urgent' : 'quiet'}>{NOTICE_LEVEL_LABEL[notice.level]}</Tag>
        {isNew ? <Tag tone="accent">New</Tag> : null}
        <strong style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{notice.title}</strong>
      </div>

      {notice.body ? (
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {notice.body}
        </p>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
          {notice.postedByName} · {postedWhen(notice.postedAt)}
        </span>
        {office ? (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: 40, fontSize: 12 }}
              onClick={() => openEditNotice(notice)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: 40, fontSize: 12, color: 'var(--urgent)' }}
              onClick={() => {
                void takeNoticeDown(notice.id);
              }}
            >
              Take down
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** When a notice went up, read in the shop's own timezone.
 *
 *  `postedAt` is a `timestamptz` — an instant, not a day — so it says the hour
 *  as well: a notice put up this morning and one put up last night are
 *  different things to somebody walking in. Cutting it back to a day here would
 *  throw away the half of it that matters. */
const postedWhen = (at: IsoInstant): string => formatInstant(at);

/* — my bench —
 *
 * The jobs this person is on, still in the shop. Everybody has one; the owner's
 * is usually short and a sprayer's is the screen. */

function MyBench() {
  const st = useApp();
  const dash = st.dash;
  const openJob = st.openJob;
  const shows = seesCustomers(st);
  const mine = dash?.mine ?? [];
  const more = (dash?.mineTotal ?? 0) - mine.length;

  return (
    <Panel>
      <PanelHead
        title="On my bench"
        meta={mine.length === 0 ? undefined : `${dash?.mineTotal ?? mine.length} in the shop`}
        action={
          dash && dash.mineOverdue > 0 ? (
            <span style={{ fontSize: 13, color: 'var(--urgent)', fontWeight: 500 }}>
              {dash.mineOverdue} overdue
            </span>
          ) : null
        }
      />
      {st.dashStatus === 'loading' && mine.length === 0 ? (
        <Empty dashed={false}>Reading your jobs…</Empty>
      ) : mine.length === 0 ? (
        <Empty>
          You are not on a job in the shop right now. The office puts people on jobs — ask them if
          you should be on one.
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {mine.map((job) => {
            const late = overdueDay(job.promisedAt);
            return (
              <button
                key={job.code}
                type="button"
                onClick={() => openJob(job.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 48,
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: late ? 'var(--urgent-tint)' : 'var(--surface-2)',
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <Swatch code={job.colour} size={26} />
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, flex: 'none' }}>
                  {job.code}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 13.5,
                  }}
                >
                  {shows ? `${job.customer} · ` : ''}
                  {job.title}
                </span>
                <Tag tone="quiet">{stageName(st, job.stageId)}</Tag>
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 12,
                    flex: 'none',
                    color: late ? 'var(--urgent)' : 'var(--ink-muted)',
                  }}
                >
                  {late ? 'Overdue ' : ''}
                  {formatDay(job.promisedAt)}
                </span>
              </button>
            );
          })}
          {more > 0 ? (
            <span style={{ fontSize: 12.5, color: 'var(--ink-muted)', padding: '2px 2px 0' }}>
              And {more} more of yours in the shop — the jobs list has all of them.
            </span>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

/* — the shop at a glance —
 *
 * The office's half. Five figures and four charts, all of them counted on the
 * server over every job the shop has. */

/** The headline figures.
 *
 *  Stat tiles rather than a chart apiece: each of these is one number, and a
 *  one-bar bar chart is a number wearing a costume. */
function Figures({ shop }: { shop: ShopOverviewDto }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      <Figure label="Jobs in the shop" value={shop.jobsInShop} />
      <Figure label="Components on the racks" value={shop.itemsInShop} />
      <Figure label="Overdue" value={shop.overdue} urgent={shop.overdue > 0} />
      <Figure label="Promised this week" value={shop.dueThisWeek} />
      <Figure label="Not promised yet" value={shop.unpromised} />
    </div>
  );
}

function Figure({
  label,
  value,
  urgent = false,
}: {
  label: string;
  value: number;
  urgent?: boolean;
}) {
  return (
    <div
      className="blueprint"
      style={{
        flex: '1 1 150px',
        minWidth: 150,
        padding: '14px 16px',
        background: urgent ? 'var(--urgent-tint)' : 'var(--surface)',
        borderColor: urgent ? 'var(--urgent)' : 'var(--line)',
      }}
    >
      <div
        style={{
          // Proportional figures, not tabular: nothing is lining up under this
          // one, and equal-width digits read loose at display sizes.
          fontSize: 30,
          fontWeight: 600,
          lineHeight: 1.1,
          color: urgent ? 'var(--urgent)' : 'var(--ink)',
        }}
      >
        {value}
      </div>
      <div className="label-xs" style={{ marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

/** What is standing at each stage, in the admin's own order and each stage's
 *  own tone.
 *
 *  The tone is the stage's, so a stage keeps its colour whatever else is on the
 *  chart — and the name is always drawn beside it, because a bar the shop has
 *  to decode by colour is a bar nobody reads. A stage over its WIP limit says
 *  so in words as well.
 *
 *  Terminal stages are not here: this is what is in the shop, and a decade of
 *  delivered jobs would be one bar and seven slivers. */
function StageLoad({ rows }: { rows: StageLoadDto[] }) {
  const st = useApp();
  const found = new Map(rows.map((r) => [r.stageId, r]));
  // The admin's order, plus any stage a job is standing at that has since been
  // archived — a job at an archived stage is still on the racks, and a chart
  // that quietly left it out would not add up to the figure above it.
  const known = st.stages.filter((s) => !s.terminal).map((s) => s.id);
  const ids = [...known, ...rows.map((r) => r.stageId).filter((id) => !known.includes(id))];
  const bars = ids.map((id) => ({ id, row: found.get(id) }));
  const most = Math.max(1, ...rows.map((r) => r.items));

  return (
    <Panel>
      <PanelHead title="In the shop" meta="components at each stage" />
      {rows.length === 0 ? (
        <Empty>Nothing is in the shop. Take a job in and it lands at the default stage.</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bars.map(({ id, row }) => {
            const stage = st.stages.find((s) => s.id === id);
            const over = Boolean(stage && stage.wip > 0 && (row?.jobs ?? 0) > stage.wip);
            return (
              <Bar
                key={id}
                label={stageName(st, id)}
                lead={<Dot tone={toneOf(st, id)} />}
                value={row?.items ?? 0}
                most={most}
                tone={toneOf(st, id) || 'var(--accent)'}
                value2={`${row?.jobs ?? 0} ${row?.jobs === 1 ? 'job' : 'jobs'}`}
                note={over ? `Over WIP ${stage?.wip}` : undefined}
              />
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/** What is promised, day by day, from today.
 *
 *  The schedule screen says which job is on which day; this says how much lands
 *  on each of them, which is the question you ask before saying yes to another
 *  kitchen. Every day is drawn, the empty ones included — a gap in the week is
 *  the whole point of looking, and a chart of only the busy days would put
 *  Monday beside Thursday and call it a fortnight. */
function PromisedAhead({ rows, today }: { rows: DayLoadDto[]; today: string }) {
  const jobs = rows.reduce((n, r) => n + r.jobs, 0);
  const total = rows.reduce((n, r) => n + r.items, 0);

  return (
    <Panel>
      <PanelHead
        title="Promised ahead"
        meta={`${total} components over the next fortnight, on ${jobs} ${jobs === 1 ? 'job' : 'jobs'}`}
      />
      {jobs === 0 ? (
        <Empty>
          Nothing is promised in the next fortnight. The schedule’s rail is where the jobs with no
          promised date are.
        </Empty>
      ) : (
        <Columns
          bars={rows.map((r) => {
            // "3 Sep" split back into its two halves, so the weekday sits over
            // the date on two lines of a narrow column.
            const [date] = formatDayShort(r.day).split(' ');
            return {
              key: r.day,
              value: r.items,
              /** The day of the week, and the day of the month under it. */
              label: DOW[weekdayOf(r.day)] ?? '',
              sub: date ?? '',
              quiet: isWeekend(r.day),
              now: r.day === today,
              say: `${formatDay(r.day)} — ${r.items} components on ${r.jobs} ${r.jobs === 1 ? 'job' : 'jobs'}`,
            };
          })}
        />
      )}
    </Panel>
  );
}

/** Goods in, week by week — what the shop has actually been taking on.
 *
 *  Counted over every job rather than the ones still here: a job that went out
 *  last week still came in the week before, and dropping it would make the shop
 *  look like it is winding down. This is the chart IDEA.md §7 is about — "can
 *  we take more work on" is a question about intake, not about the board. */
function TakenIn({ rows }: { rows: WeekLoadDto[] }) {
  const jobs = rows.reduce((n, r) => n + r.jobs, 0);

  return (
    <Panel>
      <PanelHead
        title="Goods in"
        meta={`components a week over ${DASHBOARD_WEEKS} weeks, this one included`}
      />
      {jobs === 0 ? (
        <Empty>
          Nothing has been taken in over the last {DASHBOARD_WEEKS} weeks. A job gets a goods-in
          date when the truck arrives.
        </Empty>
      ) : (
        <Columns
          bars={rows.map((r) => {
            // The Monday the week starts on — "3 Sep" as its date and its month.
            const [date, month] = formatDayShort(r.weekStart).split(' ');
            return {
              key: r.weekStart,
              value: r.items,
              label: date ?? '',
              sub: month ?? '',
              quiet: false,
              now: false,
              say: `Week of ${formatDay(r.weekStart)} — ${r.items} components in, on ${r.jobs} ${r.jobs === 1 ? 'job' : 'jobs'}`,
            };
          })}
        />
      )}
    </Panel>
  );
}

/** The colours on the racks, most components first.
 *
 *  The one chart in the app whose colour is not a decision anybody made here:
 *  each bar is the paint. That is the signature element doing its job — someone
 *  scanning wants to know what colour is in the shop before anything else — and
 *  the brand, the code and the name are all beside it, because the swatch is
 *  approximate and the code is what gets re-ordered. */
function ColourLoad({ rows }: { rows: ColourLoadDto[] }) {
  const colours = useApp((s) => s.colours);
  const most = Math.max(1, ...rows.map((r) => r.items));

  return (
    <Panel>
      <PanelHead title="On the racks" meta="by colour" />
      {rows.length === 0 ? (
        <Empty>Nothing is in the shop, so there is no colour on the racks.</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const colour = colours.find((c) => c.code === r.colour);
            return (
              <Bar
                key={r.colour}
                lead={<Swatch code={r.colour} size={20} />}
                label={colour ? colour.name : r.colour}
                code={r.colour}
                value={r.items}
                most={most}
                tone={colour?.hex ?? 'var(--accent)'}
                value2={`${r.jobs} ${r.jobs === 1 ? 'job' : 'jobs'}`}
              />
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/** Who the shop is working for, most components first, and when each of them is
 *  next owed something. */
function CustomerLoad({ rows }: { rows: CustomerLoadDto[] }) {
  const st = useApp();
  const openCustomer = st.openCustomer;
  const most = Math.max(1, ...rows.map((r) => r.items));

  if (!seesCustomers(st)) {
    return (
      <Panel>
        <PanelHead title="Working for" />
        <Empty>Not available to subcontractors.</Empty>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHead title="Working for" meta="by components in the shop" />
      {rows.length === 0 ? (
        <Empty>Nothing is in the shop. Take a job in and the customer shows up here.</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => (
            <Bar
              key={r.customerId}
              label={r.customer}
              value={r.items}
              most={most}
              tone="var(--accent)"
              value2={
                r.nextPromisedAt === null ? 'Not promised' : `Due ${formatDay(r.nextPromisedAt)}`
              }
              urgent={overdueDay(r.nextPromisedAt)}
              onOpen={() => openCustomer(r.customer)}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

/* — the marks —
 *
 * Two shapes, and nothing else: a bar lying down for a named thing, and a
 * column standing up for a day or a week. Both are thin, both sit on a
 * hairline track, and both carry their value as text — a tooltip is the
 * second way to read a number here, never the only one. */

/** One horizontal bar: what it is, how much of it there is, and a second fact
 *  beside it.
 *
 *  The fill is the colour of the thing named — a stage's tone, a paint's
 *  swatch — never a ramp over the value, which would spend the one free channel
 *  saying what the bar's own length already says. */
function Bar({
  lead,
  label,
  code,
  value,
  most,
  tone,
  value2,
  note,
  urgent = false,
  onOpen,
}: {
  lead?: ReactNode;
  label: string;
  /** A colour code, set in mono under the name. */
  code?: string;
  value: number;
  /** The biggest value on this chart — what the bar is drawn as a share of. */
  most: number;
  tone: string;
  /** The fact beside the number: how many jobs, when it is due. */
  value2: string;
  /** Something the shop has to act on — over the WIP limit. */
  note?: string;
  urgent?: boolean;
  onOpen?: () => void;
}) {
  /* One line: what it is, how long it is, and what it comes to.
   *
   * A wrapping flex rather than a grid, so the same row works in a panel the
   * full width of a desktop and in half of a tablet. The name is capped —
   * `BAR_LABEL` — because a bar chart whose labels take two thirds of the panel
   * is a list, and the bar is the thing being read. */
  const body = (
    <>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flex: `1 1 ${BAR_LABEL}px`,
          minWidth: 0,
          maxWidth: BAR_LABEL,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13.5,
        }}
      >
        {lead}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
          {code ? (
            <span
              style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-muted)' }}
            >
              {' '}
              {code}
            </span>
          ) : null}
        </span>
      </span>

      <span
        style={{
          flex: '2 1 90px',
          height: 10,
          borderRadius: 999,
          background: 'var(--bar-bg)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            // A value that is there at all gets a mark you can see — a 1 beside
            // a 400 would otherwise round to nothing and read as none.
            width: `${Math.max(value > 0 ? 2 : 0, (value / most) * 100)}%`,
            height: '100%',
            borderRadius: 999,
            background: tone,
            // The bar on the colours chart *is* the paint, and half the shop's
            // paint is a white. Without an edge a Vivid White bar disappears
            // into its own track and reads as nothing on the racks. Mixed from
            // the ink rather than a line token, so it is a definite edge under
            // a pale fill and invisible under a dark one.
            boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--ink) 20%, transparent)',
          }}
        />
      </span>

      <span
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          fontSize: 12,
          color: urgent ? 'var(--urgent)' : 'var(--ink-muted)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 13,
            color: 'var(--ink)',
            minWidth: 30,
            textAlign: 'right',
          }}
        >
          {value}
        </span>
        <span style={{ minWidth: 88, fontWeight: urgent ? 500 : 400 }}>
          {note ? <strong style={{ color: 'var(--urgent)' }}>{note} · </strong> : null}
          {value2}
        </span>
      </span>
    </>
  );

  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px 12px',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
  };

  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      style={{
        ...style,
        minHeight: 48,
        justifyContent: 'center',
        padding: '4px 6px',
        margin: '0 -6px',
        border: 0,
        borderRadius: 10,
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      {body}
    </button>
  ) : (
    <div style={style}>{body}</div>
  );
}

interface ColumnBar {
  key: string;
  /** What the column is tall for — components, the same number drawn over it.
   *  One encoding: a column drawn from the components and labelled with the
   *  jobs is two charts on top of each other, and the reader gets neither. */
  value: number;
  /** The axis label — a weekday, or a day of the month. */
  label: string;
  /** The second line of it — a date, or a month. */
  sub: string;
  /** A weekend: drawn recessive, because nobody is spraying. */
  quiet: boolean;
  /** Today, marked so the fortnight has a left-hand edge you can find. */
  now: boolean;
  /** The whole fact — both numbers — for a pointer and for a screen reader. */
  say: string;
}

/** A row of columns over an axis.
 *
 *  One series, so one colour and no legend — the panel's title names it. The
 *  value is drawn over each column that has one rather than left to the
 *  tooltip, because a number only a hover can reach is a number a shop floor
 *  never sees. The container is the plot plus the axis band, so the labels are
 *  inside it rather than cropped by it.
 *
 *  The columns are capped at `COLUMN_WIDTH` and centred in the room they are
 *  given. A fortnight spread across a desktop panel would otherwise be fourteen
 *  boxes 90px wide, which reads as a table of empty cells rather than as a
 *  chart of a quiet fortnight. */
function Columns({ bars }: { bars: ColumnBar[] }) {
  const most = Math.max(1, ...bars.map((b) => b.value));

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
      {bars.map((b) => (
        <div
          key={b.key}
          role="img"
          aria-label={b.say}
          title={b.say}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 10.5,
              color: 'var(--ink-muted)',
              height: 13,
            }}
          >
            {b.value > 0 ? b.value : ''}
          </span>
          <div
            style={{
              width: '100%',
              maxWidth: COLUMN_WIDTH,
              height: PLOT_HEIGHT,
              display: 'flex',
              alignItems: 'flex-end',
              // The track behind the column, so an empty day is still a day on
              // the chart rather than a hole in it.
              background: b.quiet ? 'var(--line-soft)' : 'var(--bar-bg)',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: '100%',
                height: `${b.value === 0 ? 0 : Math.max(4, (b.value / most) * 100)}%`,
                borderRadius: '4px 4px 0 0',
                background: 'var(--accent)',
              }}
            />
          </div>
          <span
            style={{
              fontSize: 10.5,
              lineHeight: 1.25,
              textAlign: 'center',
              color: b.now ? 'var(--ink)' : 'var(--ink-muted)',
              fontWeight: b.now ? 600 : 400,
            }}
          >
            {b.label}
            <br />
            <span style={{ fontFamily: 'var(--font-data)' }}>{b.sub}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** A stage's tone as a dot beside its name. The name is what carries the
 *  meaning; this is the second half of "colour plus label". */
function Dot({ tone }: { tone: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 10,
        height: 10,
        flex: 'none',
        borderRadius: 3,
        background: tone || 'var(--dim)',
        border: '1px solid var(--chip-border)',
      }}
    />
  );
}

/** A small uppercase pill. Three tones, all from the palette — nothing here
 *  invents a colour. */
function Tag({ tone, children }: { tone: 'quiet' | 'accent' | 'urgent'; children: ReactNode }) {
  const skin: Record<typeof tone, CSSProperties> = {
    quiet: { background: 'var(--line-soft)', color: 'var(--ink-muted)' },
    accent: { background: 'var(--accent)', color: 'var(--on-accent)' },
    urgent: { background: 'var(--urgent)', color: 'var(--on-urgent)' },
  };
  return (
    <span
      style={{
        ...skin[tone],
        flex: 'none',
        fontFamily: 'var(--font-heading)',
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** Two panels side by side where there is room, stacked where there is not. */
function TwoUp({ children }: { children: ReactNode }) {
  const narrow = useNarrowerThan(TWO_COL);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
        alignItems: 'start',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}
